import { AIClient } from "@/ai/AIAssistant";
import { LocalizedText } from "@/common/common";
import { BooleanFieldModel, RadioFieldModel, StringFieldModel } from "@/common/commonForms";
import { DCEvent, navigateTo, showGeneralError, UpdateStoreEvent } from "@/events/eventsDomain";
import { Language } from "@/i18n/languages";
import { TMessage } from "@/i18n/tt";
import { TMessageKey } from "@/i18n/types.gen";
import { LocalizedTextEditorModel } from "@/text/localizedTextEditorDomain";
import { getRoleTypeLabel, RoleType } from "@/types/enums/RoleType";
import { settingsRoleTypes } from "@/types/Settings";
import {
  Dashboard,
  DashboardCardDefinition,
  DashboardCardConfig,
  DashboardCardDefinitionRequest,
  DashboardCardType,
  DashboardOverview,
  DashboardRequest,
  DashboardStatus,
  dashboardApiClient,
  CardAction,
  cardActions,
  CardRole,
  DEFAULT_COLUMN_SPAN,
  GRID_COLUMNS,
  TemplateCardAction,
  templateCardActions,
  FieldRef,
  PersonField,
  RoleHolderField,
} from "./dashboardApiClient";
import { AccountRoleType } from "@/companyRoles/accountRoleTypeApiClient";
import { CompanyFieldRef } from "@/companyField/companyFieldDomain";
import { move } from "@/utils/arrays";
import { when } from "@/utils/functions";
import {
  mapTemplateLabels,
  TemplateDTO,
  TemplateType,
  templateTypeSegments,
} from "@/templates/templateDomain";
import type { DashboardSettingsState } from "./dashboardSettingsDomain";
import type { ToggleButtonProps } from "@/components-v2/Button";

export type DashboardForm = {
  type: "dashboard";
  dashboard?: Dashboard;
  internalName: StringFieldModel;
  title: LocalizedTextEditorModel;
  showHeading: BooleanFieldModel;
  hasDatePicker: BooleanFieldModel;
  heading: LocalizedTextEditorModel;
  status: RadioFieldModel<DashboardStatus>;
  visibleTo: string[];
  cardIds: string[];
  isSubmitting: boolean;
};

export type CardForm = {
  type: "card";
  card?: DashboardCardDefinition;
  internalName: StringFieldModel;
  heading: LocalizedTextEditorModel;
  cardType: RadioFieldModel<DashboardCardType>;
  role: RadioFieldModel<CardRole>;
  entityFields: CompanyFieldRef[];
  roles: CardRole[];
  personFields: RoleHolderField[];
  fieldFilter: StringFieldModel;
  template: RadioFieldModel<string | undefined>;
  columnSpan: RadioFieldModel<number>;
  actions: { [action in CardAction | TemplateCardAction]: BooleanFieldModel };
  isSubmitting: boolean;
};

export type DeleteTarget =
  | { kind: "dashboard"; dashboard: Dashboard }
  | { kind: "card"; card: DashboardCardDefinition };

export type DeleteDialog = {
  type: "delete";
  target: DeleteTarget;
  returnTo?: DashboardForm | CardForm;
  isSubmitting: boolean;
};

export type DashboardSettingsDialog = DashboardForm | CardForm | DeleteDialog;

const textEditor = (
  aiClient: AIClient,
  label: TMessage,
  language: Language,
  texts: LocalizedText[] = [],
) => {
  const saved =
    texts.find((text) => text.language === language && text.text.trim() !== "") ??
    texts.find((text) => text.text.trim() !== "");
  return new LocalizedTextEditorModel({
    aiClient,
    label,
    isReadOnly: () => false,
    texts: [
      {
        language: saved?.language ?? language,
        text: saved?.text ?? "",
        translationOption: { autoTranslate: false },
      },
    ],
  });
};

const statusLabels: { [key in DashboardStatus]: TMessageKey } = {
  ENABLED: "enabled",
  DISABLED: "disabled",
};

export const cardTypes = (mapTemplates: TemplateDTO[]): DashboardCardType[] => [
  "ENTITY_DATA",
  "COMPANY_ROLES",
  "ROLE_HOLDER",
  ...(mapTemplates.length > 0 ? ["TEMPLATE" as const] : []),
];

const cardTypeLabels: { [key in DashboardCardType]: TMessageKey } = {
  ENTITY_DATA: "enterprise-account.dashboards.card-type.entity-data",
  COMPANY_ROLES: "feature.company-roles",
  ROLE_HOLDER: "enterprise-account.dashboards.card-type.role-holder",
  TEMPLATE: "ownership.map",
};

export const cardTypeLabelFor = (type: DashboardCardType): TMessage => ({
  key: cardTypeLabels[type],
});

export const newDashboardForm = (
  aiClient: AIClient,
  language: Language,
  dashboard?: Dashboard,
): DashboardForm => ({
  type: "dashboard",
  dashboard,
  internalName: new StringFieldModel({ value: dashboard?.internalName, required: true }),
  title: textEditor(
    aiClient,
    { key: "enterprise-account.dashboards.dashboard-title" },
    language,
    dashboard?.title,
  ),
  showHeading: new BooleanFieldModel({ value: Boolean(dashboard?.heading?.length) }),
  hasDatePicker: new BooleanFieldModel({ value: dashboard?.hasDatePicker ?? false }),
  heading: textEditor(
    aiClient,
    { key: "enterprise-account.dashboards.page-heading" },
    language,
    dashboard?.heading,
  ),
  status: new RadioFieldModel<DashboardStatus>({
    value: dashboard?.status ?? "DISABLED",
    options: ["ENABLED", "DISABLED"],
    idFn: (status) => status,
    labelFn: (status) => ({ key: statusLabels[status] }),
  }),
  visibleTo: dashboard?.visibleTo ?? [],
  cardIds: dashboard?.cardIds ?? [],
  isSubmitting: false,
});

export const cardRoleId = (role: CardRole): string =>
  role.source === "SYSTEM" ? `SYSTEM:${role.role}` : role.roleTypeId;

export const cardRoleOptions = (roleTypes: AccountRoleType[]): CardRole[] => [
  ...settingsRoleTypes.map((role): CardRole => ({ source: "SYSTEM", role })),
  ...roleTypes.map((roleType): CardRole => ({ source: "ACCOUNT", roleTypeId: roleType.id })),
];

export const cardRoleLabel = (role: CardRole, roleTypes: AccountRoleType[]): TMessage =>
  role.source === "SYSTEM"
    ? getRoleTypeLabel(role.role)
    : {
        translated:
          roleTypes.find((roleType) => roleType.id === role.roleTypeId)?.name ?? role.roleTypeId,
      };

const personFieldLabelKeys: { [key in PersonField]: TMessageKey } = {
  NAME: "name",
  EMAIL: "email",
  PHONE_NUMBER: "enterprise-account.dashboards.person-field.phone-number",
  NATIONALITY: "enterprise-account.dashboards.person-field.nationality",
  FROM: "from",
  TO: "to",
  RESIDENTIAL_COUNTRY: "enterprise-account.dashboards.person-field.residential-country",
  RESIDENTIAL_ADDRESS_LINE1: "enterprise-account.dashboards.person-field.residential-address-line1",
  RESIDENTIAL_POSTAL_CODE: "enterprise-account.dashboards.person-field.residential-postal-code",
  RESIDENTIAL_CITY: "enterprise-account.dashboards.person-field.residential-city",
};

export const personFieldLabel = (field: PersonField): TMessage => ({
  key: personFieldLabelKeys[field],
});

const defaultHolderRole: CardRole = { source: "SYSTEM", role: RoleType.CEO };

const cardRoleField = (value: CardRole, roleTypes: AccountRoleType[]) =>
  new RadioFieldModel<CardRole>({
    value,
    options: cardRoleOptions(roleTypes),
    idFn: cardRoleId,
    labelFn: (role) => cardRoleLabel(role, roleTypes),
  });

const cardTypeField = (
  value: DashboardCardType,
  mapTemplates: TemplateDTO[],
  fieldFilter: StringFieldModel,
) =>
  new RadioFieldModel<DashboardCardType>({
    value,
    options: cardTypes(mapTemplates),
    idFn: (type) => type,
    labelFn: cardTypeLabelFor,
    onChange: () => fieldFilter.setAndCommitInputValue(""),
  });

const mapTemplateField = (value: string | undefined, mapTemplates: TemplateDTO[]) =>
  new RadioFieldModel<string | undefined>({
    value,
    options: mapTemplates.map((template) => template.id),
    idFn: (id) => id ?? "",
    labelFn: (id) => ({
      translated: mapTemplates.find((template) => template.id === id)?.name ?? "",
    }),
    descriptionFn: (id) =>
      when(mapTemplates.find((template) => template.id === id)?.data, (data) =>
        data.type === TemplateType.MAP ? { key: mapTemplateLabels[data.graphType] } : undefined,
      ),
  });

export const withMapTemplates = (
  dialog: DashboardSettingsDialog,
  mapTemplates: TemplateDTO[],
): DashboardSettingsDialog =>
  dialog.type === "card"
    ? {
        ...dialog,
        cardType: cardTypeField(dialog.cardType.value, mapTemplates, dialog.fieldFilter),
        template: mapTemplateField(dialog.template.value, mapTemplates),
      }
    : dialog;

export const openMapTemplates = (state: DashboardSettingsState): DCEvent[] => [
  navigateTo({
    name: "enterprise-account",
    params: {
      id: state.accountId,
      tab: "content",
      accountSettingsTab: templateTypeSegments[TemplateType.MAP],
    },
  }),
];

export const withRoleTypes = (
  dialog: DashboardSettingsDialog,
  roleTypes: AccountRoleType[],
): DashboardSettingsDialog =>
  dialog.type === "card"
    ? { ...dialog, role: cardRoleField(dialog.role.value, roleTypes) }
    : dialog;

export const newCardForm = (
  aiClient: AIClient,
  language: Language,
  card?: DashboardCardDefinition,
  roleTypes: AccountRoleType[] = [],
  mapTemplates: TemplateDTO[] = [],
): CardForm => {
  const fieldFilter = new StringFieldModel();
  return {
    type: "card",
    card,
    internalName: new StringFieldModel({ value: card?.internalName, required: true }),
    heading: textEditor(
      aiClient,
      { key: "enterprise-account.dashboards.card-heading" },
      language,
      card?.heading,
    ),
    cardType: cardTypeField(card?.type ?? "ENTITY_DATA", mapTemplates, fieldFilter),
    role: cardRoleField(
      card?.config.type === "ROLE_HOLDER" ? card.config.role : defaultHolderRole,
      roleTypes,
    ),
    entityFields: card?.config.type === "ENTITY_DATA" ? card.config.fields : [],
    roles: card?.config.type === "COMPANY_ROLES" ? card.config.roles : [],
    personFields: card?.config.type === "ROLE_HOLDER" ? card.config.fields : [],
    fieldFilter,
    template: mapTemplateField(
      card?.config.type === "TEMPLATE" ? card.config.templateId : undefined,
      mapTemplates,
    ),
    columnSpan: columnSpanField(
      (card?.config.type === "TEMPLATE" && card.config.columnSpan) || DEFAULT_COLUMN_SPAN,
    ),
    actions: actionFields(card?.config.type === "TEMPLATE" ? (card.config.actions ?? []) : []),
    isSubmitting: false,
  };
};

const columnSpanOptions = [
  { span: GRID_COLUMNS, label: "enterprise-account.dashboards.size.full", icon: "corners-out" },
  {
    span: DEFAULT_COLUMN_SPAN,
    label: "enterprise-account.dashboards.size.standard",
    icon: "squares-four",
  },
] as const;

const columnSpanField = (value: number) =>
  new RadioFieldModel<number>({
    value,
    options: columnSpanOptions.map(({ span }) => span),
    idFn: String,
    labelFn: (span) => ({
      key:
        columnSpanOptions.find((option) => option.span === span)?.label ??
        "enterprise-account.dashboards.size",
    }),
  });

export const columnSpanButtons = (form: CardForm): ToggleButtonProps[] =>
  columnSpanOptions.map(({ span, label, icon }) => ({
    label: { key: label },
    icon,
    variant: "basic",
    disableMinWidth: true,
    selected: form.columnSpan.value === span,
    onClick: () => form.columnSpan.setValue(span),
  }));

export const editableActions = [...cardActions, ...templateCardActions];

const actionFields = (chosen: string[]): CardForm["actions"] => ({
  EXPAND: new BooleanFieldModel({ value: chosen.includes("EXPAND") }),
  DOWNLOAD: new BooleanFieldModel({ value: chosen.includes("DOWNLOAD") }),
});

export const updateDialog = (
  accountId: string,
  update: (dialog: DashboardSettingsDialog) => DashboardSettingsDialog | undefined,
): UpdateStoreEvent => ({
  type: "update-store",
  data: {
    dashboardSettings: (state) =>
      state?.accountId === accountId && state.dialog
        ? { ...state, dialog: update(state.dialog) }
        : state,
  },
});

const updateDashboardForm = (
  accountId: string,
  update: (form: DashboardForm) => Partial<DashboardForm>,
): UpdateStoreEvent =>
  updateDialog(accountId, (dialog) =>
    dialog.type === "dashboard" ? { ...dialog, ...update(dialog) } : dialog,
  );

const openDialog = (accountId: string, dialog: DashboardSettingsDialog): UpdateStoreEvent => ({
  type: "update-store",
  data: {
    dashboardSettings: (state) => (state?.accountId === accountId ? { ...state, dialog } : state),
  },
});

export const openDashboardDialog = (
  state: DashboardSettingsState,
  aiClient: AIClient,
  dashboard?: Dashboard,
): DCEvent[] => [
  openDialog(state.accountId, newDashboardForm(aiClient, state.language, dashboard)),
];

export const openCardDialog = (
  state: DashboardSettingsState,
  aiClient: AIClient,
  card?: DashboardCardDefinition,
): DCEvent[] => [
  openDialog(
    state.accountId,
    newCardForm(aiClient, state.language, card, state.roleTypes, state.mapTemplates),
  ),
];

export const openDeleteDialog = (
  state: DashboardSettingsState,
  target: DeleteTarget,
  returnTo?: DashboardForm | CardForm,
): DCEvent[] => [
  openDialog(state.accountId, { type: "delete", target, returnTo, isSubmitting: false }),
];

export const cancelDelete = (state: DashboardSettingsState): DCEvent[] => [
  updateDialog(state.accountId, (dialog) =>
    dialog.type === "delete" ? dialog.returnTo : undefined,
  ),
];

export const closeDialog = (state: DashboardSettingsState): DCEvent[] => [
  updateDialog(state.accountId, () => undefined),
];

export const addVisibleGroup = (state: DashboardSettingsState, grantee: string): DCEvent[] => [
  updateDashboardForm(state.accountId, (form) => ({
    visibleTo: withItem(form.visibleTo, grantee, idOfString),
  })),
];

export const removeVisibleGroup = (state: DashboardSettingsState, grantee: string): DCEvent[] => [
  updateDashboardForm(state.accountId, (form) => ({
    visibleTo: withoutItem(form.visibleTo, grantee, idOfString),
  })),
];

export const addDashboardCard = (state: DashboardSettingsState, cardId: string): DCEvent[] => [
  updateDashboardForm(state.accountId, (form) => ({
    cardIds: withItem(form.cardIds, cardId, idOfString),
  })),
];

export const removeDashboardCard = (state: DashboardSettingsState, cardId: string): DCEvent[] => [
  updateDashboardForm(state.accountId, (form) => ({
    cardIds: withoutItem(form.cardIds, cardId, idOfString),
  })),
];

export const moveDashboardCard = (
  state: DashboardSettingsState,
  cardId: string,
  toCardId: string,
): DCEvent[] => [
  updateDashboardForm(state.accountId, (form) => ({
    cardIds: withItemMoved(form.cardIds, cardId, toCardId, idOfString),
  })),
];

export const editorTexts = (editor: LocalizedTextEditorModel): LocalizedText[] =>
  editor.textModels.map((text) => ({
    language: text.locale,
    text: text.model.inputValue,
    translationOption: editor.translationOption,
  }));

export const fieldRefId = (field: FieldRef<string>): string =>
  field.source === "SYSTEM" ? `SYSTEM:${field.field}` : field.definitionId;

const updateCardForm = (
  accountId: string,
  update: (form: CardForm) => Partial<CardForm>,
): UpdateStoreEvent =>
  updateDialog(accountId, (dialog) =>
    dialog.type === "card" ? { ...dialog, ...update(dialog) } : dialog,
  );

const withItem = <T>(items: T[], item: T, idOf: (item: T) => string): T[] =>
  items.some((other) => idOf(other) === idOf(item)) ? items : [...items, item];

const withoutItem = <T>(items: T[], id: string, idOf: (item: T) => string): T[] =>
  items.filter((item) => idOf(item) !== id);

const withItemMoved = <T>(items: T[], id: string, toId: string, idOf: (item: T) => string): T[] => {
  const moved = items.find((item) => idOf(item) === id);
  const toIndex = items.findIndex((item) => idOf(item) === toId);
  return moved && toIndex >= 0 ? move(items, moved, toIndex) : items;
};

const idOfString = (id: string) => id;

export const addEntityField = (
  state: DashboardSettingsState,
  field: CompanyFieldRef,
): DCEvent[] => [
  updateCardForm(state.accountId, (form) => ({
    entityFields: withItem(form.entityFields, field, fieldRefId),
  })),
];

export const removeEntityField = (state: DashboardSettingsState, id: string): DCEvent[] => [
  updateCardForm(state.accountId, (form) => ({
    entityFields: withoutItem(form.entityFields, id, fieldRefId),
  })),
];

export const moveEntityField = (
  state: DashboardSettingsState,
  id: string,
  toId: string,
): DCEvent[] => [
  updateCardForm(state.accountId, (form) => ({
    entityFields: withItemMoved(form.entityFields, id, toId, fieldRefId),
  })),
];

export const addCardRole = (state: DashboardSettingsState, role: CardRole): DCEvent[] => [
  updateCardForm(state.accountId, (form) => ({ roles: withItem(form.roles, role, cardRoleId) })),
];

export const removeCardRole = (state: DashboardSettingsState, id: string): DCEvent[] => [
  updateCardForm(state.accountId, (form) => ({ roles: withoutItem(form.roles, id, cardRoleId) })),
];

export const moveCardRole = (
  state: DashboardSettingsState,
  id: string,
  toId: string,
): DCEvent[] => [
  updateCardForm(state.accountId, (form) => ({
    roles: withItemMoved(form.roles, id, toId, cardRoleId),
  })),
];

export const addPersonField = (
  state: DashboardSettingsState,
  field: RoleHolderField,
): DCEvent[] => [
  updateCardForm(state.accountId, (form) => ({
    personFields: withItem(form.personFields, field, fieldRefId),
  })),
];

export const removePersonField = (state: DashboardSettingsState, id: string): DCEvent[] => [
  updateCardForm(state.accountId, (form) => ({
    personFields: withoutItem(form.personFields, id, fieldRefId),
  })),
];

export const movePersonField = (
  state: DashboardSettingsState,
  id: string,
  toId: string,
): DCEvent[] => [
  updateCardForm(state.accountId, (form) => ({
    personFields: withItemMoved(form.personFields, id, toId, fieldRefId),
  })),
];

const hasText = (texts: LocalizedText[]) => texts.some((text) => text.text.trim() !== "");

export const canSaveDashboard = (form: DashboardForm): boolean =>
  form.internalName.inputValue.trim() !== "" && hasText(editorTexts(form.title));

export const canSaveCard = (form: CardForm): boolean =>
  form.internalName.inputValue.trim() !== "" &&
  hasText(editorTexts(form.heading)) &&
  (form.cardType.value !== "TEMPLATE" || form.template.value !== undefined);

export const dashboardRequest = (form: DashboardForm): DashboardRequest => ({
  internalName: form.internalName.inputValue.trim(),
  title: editorTexts(form.title),
  heading: form.showHeading.value ? editorTexts(form.heading) : undefined,
  hasDatePicker: form.hasDatePicker.value === true,
  status: form.status.value,
  cardIds: form.cardIds,
  visibleTo: form.visibleTo,
});

export const cardConfig = (form: CardForm): DashboardCardConfig => {
  switch (form.cardType.value) {
    case "ENTITY_DATA":
      return { type: "ENTITY_DATA", fields: form.entityFields };
    case "COMPANY_ROLES":
      return { type: "COMPANY_ROLES", roles: form.roles };
    case "ROLE_HOLDER":
      return { type: "ROLE_HOLDER", role: form.role.value, fields: form.personFields };
    case "TEMPLATE":
      return {
        type: "TEMPLATE",
        templateId: form.template.value ?? "",
        columnSpan: form.columnSpan.value,
        actions: editableActions.filter((action) => form.actions[action].value),
      };
  }
};

export const cardRequest = (form: CardForm): DashboardCardDefinitionRequest => ({
  internalName: form.internalName.inputValue.trim(),
  heading: editorTexts(form.heading),
  config: cardConfig(form),
});

const submitting = (accountId: string, isSubmitting: boolean): UpdateStoreEvent =>
  updateDialog(accountId, (dialog) => ({ ...dialog, isSubmitting }));

const savedCallbacks = (accountId: string) => ({
  onSuccess: (res: { data: DashboardOverview }): DCEvent[] => [
    {
      type: "update-store",
      data: {
        dashboardSettings: (state) =>
          state?.accountId === accountId
            ? { ...state, overview: res.data, dialog: undefined }
            : state,
      },
    },
  ],
  onFailure: (): DCEvent[] => [submitting(accountId, false), showGeneralError()],
});

export const saveDashboard = (state: DashboardSettingsState, form: DashboardForm): DCEvent[] => {
  if (form.isSubmitting) return [];
  const request = dashboardRequest(form);
  const callbacks = savedCallbacks(state.accountId);
  return [
    submitting(state.accountId, true),
    form.dashboard
      ? dashboardApiClient.updateDashboard(state.accountId, form.dashboard.id, request, callbacks)
      : dashboardApiClient.addDashboard(state.accountId, request, callbacks),
  ];
};

export const saveCard = (state: DashboardSettingsState, form: CardForm): DCEvent[] => {
  if (form.isSubmitting) return [];
  const request = cardRequest(form);
  const callbacks = savedCallbacks(state.accountId);
  return [
    submitting(state.accountId, true),
    form.card
      ? dashboardApiClient.updateCard(state.accountId, form.card.id, request, callbacks)
      : dashboardApiClient.addCard(state.accountId, request, callbacks),
  ];
};

export const confirmDelete = (state: DashboardSettingsState, dialog: DeleteDialog): DCEvent[] => {
  if (dialog.isSubmitting) return [];
  const callbacks = savedCallbacks(state.accountId);
  return [
    submitting(state.accountId, true),
    dialog.target.kind === "dashboard"
      ? dashboardApiClient.deleteDashboard(state.accountId, dialog.target.dashboard.id, callbacks)
      : dashboardApiClient.deleteCard(state.accountId, dialog.target.card.id, callbacks),
  ];
};

export const deleteMessage = (dialog: DeleteDialog): TMessage => {
  switch (dialog.target.kind) {
    case "dashboard":
      return {
        key: "enterprise-account.dashboards.delete-dashboard.message",
        params: { name: dialog.target.dashboard.internalName },
      };
    case "card":
      return dialog.target.card.dashboardCount === 0
        ? {
            key: "enterprise-account.dashboards.delete-unused-card.message",
            params: { name: dialog.target.card.internalName },
          }
        : {
            key: "enterprise-account.dashboards.delete-card.message",
            params: {
              name: dialog.target.card.internalName,
              count: dialog.target.card.dashboardCount,
            },
          };
  }
};

export const textFor = (texts: LocalizedText[], language: string): string | undefined =>
  (
    texts.find((text) => text.language === language && text.text.trim() !== "") ??
    texts.find((text) => text.text.trim() !== "")
  )?.text;
